import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
} from 'omakase/utils/OmakasePolymorphicComponent';

import { PadButtonDefaultElement } from 'omakase/PadButton/PadButtonCore';
import { Switcher, SwitcherProps, SwitcherTypeGuard } from 'omakase';
import { handleBlurFormField, handleChangeFormField } from '@tock/shared/ui/forms/formHelpers';
import { omit } from 'es-toolkit/compat';
import { useField } from 'formik';
import React from 'react';

type FormSwitcherRadioButtonProps = Omit<SwitcherProps, 'checked' | 'type'> & {
  component?: React.ReactNode;
};

/**
 * (Formik) implementation of Switcher
 */
const FormSwitcherRadioButton: OmakasePolymorphicComponent<
  FormSwitcherRadioButtonProps,
  typeof PadButtonDefaultElement,
  SwitcherTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof PadButtonDefaultElement>(
    {
      children,
      component,
      name,
      value,
      ...rest
    }: OmakasePolymorphicComponentProps<FormSwitcherRadioButtonProps, E, SwitcherTypeGuard>,
    ref: React.Ref<HTMLInputElement> | undefined
  ) => {
    const [fieldConfig] = useField(name);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      handleChangeFormField(
        fieldConfig,
        event,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rest.onChange as (evt: React.ChangeEvent<any>) => void
      );

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>
      handleBlurFormField(fieldConfig, event, rest.onBlur);

    const Component: React.ElementType = component || PadButtonDefaultElement;

    // Destructuring out onChange, onBlur, and value to prevent them from being declared twice on Switcher
    const trimmedFieldConfig = omit(fieldConfig, ['onChange', 'onBlur', 'value']);

    return (
      <Switcher
        onChange={handleChange}
        onBlur={handleBlur}
        type="radio"
        component={Component}
        ref={ref}
        value={value}
        checked={fieldConfig.value === value}
        {...trimmedFieldConfig}
        {...rest}
      />
    );
  }
);

export default FormSwitcherRadioButton;
